import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useAuthStore } from '@/stores/auth-store'
import { LanguageSwitcher } from '@/components/language-switcher'

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<'form'>) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const { login, isLoading, error, clearError } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()

  const from = location.state?.from?.pathname || '/'

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      await login(email, password)
      navigate(from, { replace: true })
    } catch {
      // Error is already set in the store
    }
  }

  return (
    <form
      className={cn('flex flex-col gap-6', className)}
      onSubmit={handleSubmit}
      {...props}
    >
      <FieldGroup>
        <div className='flex justify-end'>
          <LanguageSwitcher />
        </div>
        <div className='flex flex-col items-center gap-1 text-center'>
          <h1 className='text-2xl font-bold'>{t('login.title')}</h1>
          <p className='text-muted-foreground text-sm text-balance'>
            {t('login.subtitle')}
          </p>
        </div>
        {error && (
          <div className='bg-destructive/10 text-destructive text-sm p-3 rounded-md'>
            {error}
          </div>
        )}
        <Field>
          <FieldLabel htmlFor='email'>{t('login.email')}</FieldLabel>
          <Input
            id='email'
            type='email'
            placeholder={t('login.emailPlaceholder')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (error) clearError()
            }}
            required
            disabled={isLoading}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor='password'>{t('login.password')}</FieldLabel>
          <Input
            id='password'
            type='password'
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              if (error) clearError()
            }}
            required
            disabled={isLoading}
          />
        </Field>
        <Field>
          <Button type='submit' disabled={isLoading}>
            {isLoading ? t('login.loggingIn') : t('login.loginButton')}
          </Button>
        </Field>
      </FieldGroup>
    </form>
  )
}
